/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    ArrayExtensions,
    getPriceForProductSearchResult,
    PriceForProductSearchResultInput
} from '@msdyn365-commerce-modules/retail-actions';
import {
    ICategoryHierarchyViewProps,
    IRefineMenuViewProps,
    ISearchResultContainerViewProps,
    ISearchResultModalViewProps,
    ISortByViewProps,
    ITitleViewProps
} from '@msdyn365-commerce-modules/search-result-container';
import { Button, getTelemetryObject, Module, Node } from '@msdyn365-commerce-modules/utilities';
import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import { IPriceComponentResources } from '@msdyn365-commerce/components';
import MsDyn365, { IImageSettings } from '@msdyn365-commerce/core';
import { ProductPrice, ProductRefinerSource, ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import React, { MouseEventHandler, useEffect, useState } from 'react';
import {
    ISearchResultContainerProps,
    ISearchResultContainerResources
} from '../definition-extensions/search-result-container.ext.props.autogenerated';
import { ProductComponent } from './components/product.component';

/**
 * DescriptionInterval.
 */
enum DescriptionInterval {
    productDescriptionInterval = 14,
    productDescriptionIntervalRemainder0 = 0,
    productDescriptionIntervalRemainder9 = 9
}

// Hardcoded image settings for search result container - 1:1 square ratio
// Dimensions are hardcoded, but mode/quality/lazyload will be merged from Site Builder
const baseImageSettings: IImageSettings = {
    viewports: {
        xs: { w: 477, h: 477 },
        sm: { w: 784, h: 784 },
        md: { w: 223, h: 223 },
        lg: { w: 335, h: 335 },
        xl: { w: 407, h: 407 }
    },
    cropFocalRegion: true,
    mode: 5
};

const filterPriceKeyword = 'price';

/**
 * Merge hardcoded image settings with Site Builder configuration.
 * @param base - Base image settings with hardcoded dimensions.
 * @param siteBuilder - Image settings from Site Builder (contains mode, quality, lazyload).
 * @returns Merged image settings with dynamic parameters injected into query strings.
 */
const mergeImageSettings = (base: IImageSettings, siteBuilder?: IImageSettings): IImageSettings => {
    if (!siteBuilder) {
        return base;
    }

    const merged: IImageSettings = { ...base };

    // Only override if Site Builder has defined values
    if (siteBuilder.lazyload !== undefined) {
        merged.lazyload = siteBuilder.lazyload;
    }
    if (siteBuilder.mode !== undefined) {
        merged.mode = siteBuilder.mode;
    }
    if (siteBuilder.quality !== undefined) {
        merged.quality = siteBuilder.quality;
    }

    // Build dynamic query strings for each viewport with quality and mode
    const quality = merged.quality ?? 80;
    const mode = merged.mode ?? 5;

    if (merged.viewports) {
        Object.keys(merged.viewports).forEach(viewport => {
            const vp = merged.viewports[viewport as keyof typeof merged.viewports];
            if (vp && vp.w && vp.h) {
                vp.q = `w=${vp.w}&h=${vp.h}&q=${quality}&m=${mode}&f=webp&cropfocalregion=true`;
            }
        });
    }

    return merged;
};
/**
 * Returns the product inventory label.
 * @param  channelInventoryConfigurationId - The channel configuration Id.
 * @param  product - The product.
 * @returns The inventory label.
 */
function getInventoryLabel(channelInventoryConfigurationId: number | undefined, product: ProductSearchResult): string | undefined {
    if (!channelInventoryConfigurationId || !ArrayExtensions.hasElements(product.AttributeValues)) {
        return undefined;
    }
    const inventoryAttribute = product.AttributeValues.find(attribute => attribute.RecordId === channelInventoryConfigurationId);
    if (inventoryAttribute) {
        return inventoryAttribute.TextValue;
    }
    return undefined;
}

/**
 * Render Clear button for mobile viewport.
 * @param props - The view props.
 * @param product - The product item data.
 * @param index - The index of each product item.
 * @param isEnabledProductDescription - The flag for showing Product desription tile.
 * @param imageSettings - The image settings based on layout.
 * @param productPrices - Map of product prices indexed by product ID.
 * @returns The JSX Element.
 */
const renderProductCardsDescription = (
    props: ISearchResultContainerViewProps & ISearchResultContainerProps<{}>,
    product: ProductSearchResult,
    index: number,
    isEnabledProductDescription?: boolean,
    imageSettings?: IImageSettings,
    productPrices?: Map<number, ProductPrice>,
    hideAffiliatePricesUntilLoaded?: boolean
): JSX.Element | null => {
    const { config, context, resources, productsDimensionAvailabilities } = props;
    const telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
    const breadCrumbType = props.context.app.config.breadcrumbType;
    const isAllowBack = props.config.allowBackNavigation && (breadCrumbType === 'back' || breadCrumbType === 'categoryAndBack');
    const classname = isEnabledProductDescription
        ? 'ms-product-search-result__item product__description'
        : 'ms-product-search-result__item';
    const quickviewslot = ArrayExtensions.hasElements(props.slots.quickview) ? props.slots.quickview[0] : undefined;
    const channelInventoryConfigurationId = props.data.products.result?.channelInventoryConfigurationId;
    const isPriceMinMaxFeatureState = props.data.featureState.result?.find(
        featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature'
    );
    const priceResources: IPriceComponentResources = {
        priceRangeSeparator: resources.priceRangeSeparator
    };

    const productComparisonButton = ArrayExtensions.hasElements(props.slots.productComparisonButton)
        ? props.slots.productComparisonButton[0]
        : null;

    // Get affiliate price for this product if available
    const productPrice = productPrices?.get(product.RecordId);

    return (
        <li className={classname} key={index}>
            <ProductComponent
                context={context}
                telemetryContent={telemetryContent}
                imageSettings={imageSettings || mergeImageSettings(baseImageSettings, config.imageSettings)}
                freePriceText={resources.priceFree}
                originalPriceText={resources.originalPriceText}
                currentPriceText={resources.currentPriceText}
                hidePrice={config.disablePrices}
                showPriceSkeleton={!config.disablePrices && hideAffiliatePricesUntilLoaded}
                hideUnitPrice={config.disablePrices}
                ratingAriaLabel={resources.ratingAriaLabel}
                allowBack={isAllowBack}
                id={props.id}
                key={product.RecordId}
                typeName={props.typeName}
                data={{ product, productPrice }}
                quickViewButton={quickviewslot}
                productComparisonButton={productComparisonButton}
                isEnabledProductDescription={isEnabledProductDescription}
                inventoryLabel={getInventoryLabel(channelInventoryConfigurationId, product)}
                isPriceMinMaxEnabled={isPriceMinMaxFeatureState?.IsEnabled}
                priceResources={priceResources}
                swatchItemAriaLabel={resources.swatchItemAriaLabel}
                dimensionAvailabilities={productsDimensionAvailabilities?.find(
                    (dimensionAvailability: IProductsDimensionsAvailabilities[]) =>
                        ArrayExtensions.hasElements(dimensionAvailability) &&
                        dimensionAvailability[0].masterProductId === product.MasterProductId
                )}
            />
        </li>
    );
};

/**
 * Render Clear button for mobile viewport.
 * @param props - The view props.
 * @param imageSettings - The image settings based on layout.
 * @param productPrices - Map of product prices indexed by product ID.
 * @returns The JSX Element.
 */
const renderProducts = (
    props: ISearchResultContainerViewProps & ISearchResultContainerProps<{}>,
    imageSettings?: IImageSettings,
    productPrices?: Map<number, ProductPrice>,
    hideAffiliatePricesUntilLoaded?: boolean
): JSX.Element | null => {
    const products = props.data.listPageState.result?.activeProducts;
    if (!products || !ArrayExtensions.hasElements(products)) {
        return null;
    }
    const productDescriptionInterval: number = DescriptionInterval.productDescriptionInterval;
    const productDescriptionIntervalRemainder0: number = DescriptionInterval.productDescriptionIntervalRemainder0;
    const productDescriptionIntervalRemainder9: number = DescriptionInterval.productDescriptionIntervalRemainder9;
    return (
        <Node {...props.ProductsContainer}>
            {props.errorMessage}
            <ul className='list-unstyled'>
                {products.map((product: ProductSearchResult, index: number) =>
                    index % productDescriptionInterval === productDescriptionIntervalRemainder0 ||
                    index % productDescriptionInterval === productDescriptionIntervalRemainder9
                        ? renderProductCardsDescription(
                              props,
                              product,
                              index,
                              props.config.enableProductDescription,
                              imageSettings,
                              productPrices,
                              hideAffiliatePricesUntilLoaded
                          )
                        : renderProductCardsDescription(
                              props,
                              product,
                              index,
                              props.config.enableProductDescription,
                              imageSettings,
                              productPrices,
                              hideAffiliatePricesUntilLoaded
                          )
                )}
            </ul>
        </Node>
    );
};

/**
 * Render Title Count.
 * @param props - The title view props.
 * @returns The JSX Element.
 */
const renderTitleCount = (props: ITitleViewProps): JSX.Element | null => {
    const { title, TitleContainer } = props;
    if (title) {
        return <Node {...TitleContainer}>{title.titleCount}</Node>;
    }
    return null;
};

/**
 * Render Category Hierarchy.
 * @param props - The category hierarchy view props.
 * @returns The JSX Element.
 */
const renderCategoryHierarchy = (props: ICategoryHierarchyViewProps): JSX.Element | null => {
    const { CategoryHierarchyContainer, categoryHierarchyList, categoryHierarchySeparator } = props;
    if (categoryHierarchyList) {
        return (
            <Node {...CategoryHierarchyContainer}>
                {categoryHierarchyList.map((category, index) => (
                    <React.Fragment key={index}>
                        {category}
                        {categoryHierarchyList[index + 1] && categoryHierarchySeparator}
                    </React.Fragment>
                ))}
            </Node>
        );
    }

    return null;
};

/**
 * Function to make filter sticky.
 * @param isMobile - The boolean prop.
 * @param isStickyHeader - The boolean prop.
 */
export const configureStickyFilter = (isMobile: boolean, isStickyHeader: boolean): void => {
    // Get heights of cookie and promotion banners
    const defaultValue = 0;
    const defaultOffset = 28;
    const headerContainer: HTMLElement | null = document.querySelector('.ms-header');

    const headerHeight: number | undefined = headerContainer?.offsetHeight;
    const containerHeight: number = headerHeight ?? defaultOffset;
    const bannerHeights = isStickyHeader ? defaultValue : containerHeight;
    const headerElement = document.querySelector('header .lock-opaque');
    const defaultContainer = document.querySelector('header .default-container');
    const refinerElement = isMobile ? document.querySelector('.msc-sort-filter-wrapper') : document.querySelector('.ms-refiner-heading');
    if (refinerElement && headerElement && defaultContainer) {
        if (MsDyn365.isBrowser && document.documentElement.scrollTop > bannerHeights) {
            refinerElement.classList.add('lock-Filter');
            refinerElement.setAttribute('style', `top: ${defaultContainer.clientHeight}px`);
        } else {
            refinerElement.classList.remove('lock-Filter');
        }
    } else if (refinerElement) {
        if (MsDyn365.isBrowser && document.documentElement.scrollTop > bannerHeights) {
            refinerElement.classList.add('lock-top');
        } else {
            refinerElement.classList.remove('lock-top');
            refinerElement.classList.remove('lock-Filter');
            refinerElement.removeAttribute('style');
        }
    }
};

/**
 * Function to setup filter sticky.
 * @param isMobile - The boolean prop.
 * @param isStickyHeader - The boolean prop.
 */
function stickyFilterSetup(isMobile: boolean, isStickyHeader: boolean): void {
    window.addEventListener('scroll', () => {
        configureStickyFilter(isMobile, isStickyHeader);
    });
    configureStickyFilter(isMobile, isStickyHeader);
}

/**
 * Function to make filter sticky.
 * @param refinerRefernce - Ref prop for filter.
 * @param toggle - Toggle function for filter menu.
 */
function useOutsideClick(refinerRefernce: React.RefObject<HTMLElement>, toggle: (event: Event) => void) {
    React.useEffect(() => {
        /**
         * Function to make filter sticky.
         * @param event - Event prop for filter.
         */
        function handleClickOutside(event: Event) {
            const element = document.querySelector('.ms-refine-filter__toggle_collapsed');
            if (refinerRefernce.current && !refinerRefernce.current.contains(event.target as Node) && !element) {
                toggle(event);
            }
        }
        if (MsDyn365.isBrowser) {
            document.addEventListener('click', handleClickOutside);
        }
        return () => {
            if (MsDyn365.isBrowser) {
                document.removeEventListener('click', handleClickOutside);
            }
        };
    }, [refinerRefernce, toggle]);
}

/**
 * Render the category page items.
 * @param props - The view props.
 * @returns The search Result container module view.
 */
const SearchResultContainerView: React.FC<ISearchResultContainerViewProps & ISearchResultContainerProps<{}>> = props => {
    const {
        SearchResultContainer,
        pagination,
        ProductSectionContainer,
        choiceSummary,
        isMobile,
        modalToggle,
        searchResultModal,
        TitleViewProps,
        refineMenu,
        categoryHierarchy,
        sortByOptions,
        CategoryNavContainer,
        RefineAndProductSectionContainer,
        FeatureSearchContainer,
        similarLookProduct,
        errorMessage,
        resources,
        config
    } = props;

    const shouldUseAffiliatePricing =
        !!config.enableAffiliationBasedPricing && props.context.actionContext.requestContext.user.isAuthenticated;

    // State for affiliate pricing
    const [productPrices, setProductPrices] = useState<Map<number, ProductPrice>>(new Map());
    const [isAffiliatePricingLoading, setIsAffiliatePricingLoading] = useState<boolean>(shouldUseAffiliatePricing);

    // Fetch affiliate prices if enabled and user is authenticated
    useEffect(() => {
        const fetchAffiliatePrices = async (): Promise<void> => {
            const products = props.data.listPageState.result?.activeProducts;
            if (!shouldUseAffiliatePricing || !MsDyn365.isBrowser) {
                setIsAffiliatePricingLoading(false);
                return;
            }

            if (!products || !ArrayExtensions.hasElements(products)) {
                setProductPrices(new Map());
                setIsAffiliatePricingLoading(false);
                return;
            }

            setIsAffiliatePricingLoading(true);

            try {
                const updatedProducts = await getPriceForProductSearchResult(
                    new PriceForProductSearchResultInput(products),
                    props.context.actionContext
                );

                // Create a map of product prices indexed by product ID.
                const pricesMap = new Map<number, ProductPrice>();
                updatedProducts.forEach(updatedProduct => {
                    pricesMap.set(updatedProduct.RecordId, {
                        BasePrice: updatedProduct.BasePrice,
                        AdjustedPrice: updatedProduct.Price,
                        CustomerContextualPrice: updatedProduct.Price
                    });
                });

                setProductPrices(pricesMap);
            } catch (error) {
                setProductPrices(new Map());
                // Log error but don't break rendering - fall back to default prices after loading finishes.
                // eslint-disable-next-line no-console -- Log error for debugging
                console.error('Error fetching affiliate prices:', error);
            } finally {
                setIsAffiliatePricingLoading(false);
            }
        };

        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- Async operation for price fetching
        fetchAffiliatePrices();
    }, [props.data.listPageState.result?.activeProducts, props.context.actionContext, shouldUseAffiliatePricing]);

    const hideAffiliatePricesUntilLoaded = shouldUseAffiliatePricing && isAffiliatePricingLoading;

    useEffect(() => {
        if (config.useStickyFilter && MsDyn365.isBrowser) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- app context is generic
            stickyFilterSetup(props.isMobile, props.context.app.config.enableStickyHeader as boolean);
        }
    }, [props]);
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const toggle = () => {
        setIsExpanded(!isExpanded);
    };
    const wrapperRefiner = React.useRef(null);
    useOutsideClick(wrapperRefiner, toggle);
    const isRecoSearchPage = props.context.actionContext.requestContext.query?.recommendation;
    if (isMobile) {
        return (
            <Module {...SearchResultContainer}>
                {similarLookProduct ? null : (
                    <Node {...CategoryNavContainer}>
                        {renderCategoryHierarchy(categoryHierarchy)}
                        {renderTitle(TitleViewProps)}
                        {config.enableProductCount && renderTitleCount(TitleViewProps)}
                    </Node>
                )}
                {choiceSummary}
                <Node className='msc-sort-filter-wrapper'>{modalToggle}</Node>
                {createSearchResultModal(searchResultModal, refineMenu, sortByOptions, isRecoSearchPage)}
                <Node {...FeatureSearchContainer}>{similarLookProduct}</Node>
                {errorMessage}
                {renderProducts(
                    props,
                    config.imageSettings ? mergeImageSettings(baseImageSettings, config.imageSettings) : baseImageSettings,
                    productPrices,
                    hideAffiliatePricesUntilLoaded
                )}
                {pagination}
            </Module>
        );
    }
    return (
        <Module {...SearchResultContainer}>
            {similarLookProduct ? null : (
                <Node {...CategoryNavContainer}>
                    {categoryHierarchy && renderCategoryHierarchy(categoryHierarchy)}
                    {TitleViewProps && renderTitle(TitleViewProps)}
                    {config.enableProductCount && TitleViewProps && renderTitleCount(TitleViewProps)}
                </Node>
            )}
            <Node {...FeatureSearchContainer}>{similarLookProduct}</Node>
            <Node {...RefineAndProductSectionContainer}>
                <Node ref={wrapperRefiner} className='ms-refiner-heading'>
                    <Node className='ms-refiner-heading_title'>
                        <Button
                            className={isExpanded ? 'ms-refine-filter__toggle_expanded' : 'ms-refine-filter__toggle_collapsed'}
                            aria-label='Filter'
                            onClick={toggle}
                            aria-expanded={isExpanded}
                            tabIndex={0}
                        >
                            {resources.filterText}
                        </Button>
                        <div className='ms-search-result-wrapper-title-choice-summary'>{choiceSummary}</div>
                        <div className='ms-search-result-wrapper-sort-by-category'>
                            {sortByOptions && !isRecoSearchPage && renderSort(sortByOptions, isRecoSearchPage)}
                        </div>
                    </Node>
                    {refineMenu && isExpanded && renderRefiner(refineMenu)}
                    {config.enableRefinerFooter && (
                        <Node className='ms-refine__footer'>
                            {refineMenu && isExpanded && renderRefinerFooter(refineMenu, toggle, resources)}
                        </Node>
                    )}
                </Node>

                <Node {...ProductSectionContainer}>
                    {errorMessage}
                    {renderProducts(
                        props,
                        config.imageSettings ? mergeImageSettings(baseImageSettings, config.imageSettings) : baseImageSettings,
                        productPrices,
                        hideAffiliatePricesUntilLoaded
                    )}
                    {pagination}
                </Node>
            </Node>
        </Module>
    );
};

/**
 * Render Search result Modal.
 * @param modalProps - The Search Result Modal view props.
 * @param refineMenu - The Refine Menu view props.
 * @param sortByDropDown - The Sort by view props.
 * @param isRecoSearchPage - The recomandation search page flag.
 * @returns The JSX Element.
 */
const createSearchResultModal = (
    modalProps: ISearchResultModalViewProps,
    refineMenu: IRefineMenuViewProps,
    sortByDropDown: ISortByViewProps,
    isRecoSearchPage?: string
): JSX.Element => {
    return React.cloneElement(
        modalProps.modal,
        {},
        modalProps.modalHeader,
        createModalBody(modalProps, refineMenu, sortByDropDown, isRecoSearchPage),
        modalProps.modalFooter
    );
};

/**
 * Render Refiner Mobile.
 * @param props - The Refine Menu view props.
 * @returns The JSX Element.
 */
const renderRefinerMobile = (props: IRefineMenuViewProps): JSX.Element | null => {
    const { refiners, RefineMenuContainer, RefinerSectionContainer } = props;

    if (!refiners) {
        return null;
    }

    const filteredRefiners = refiners.filter(refiner => {
        if (!React.isValidElement(refiner)) {
            return true;
        }
        return refiner.props.productRefinerHierarchy.SourceValue !== ProductRefinerSource.Price;
    });

    if (filteredRefiners) {
        return (
            <Node {...RefinerSectionContainer}>
                <Node {...RefineMenuContainer}>
                    {filteredRefiners.map((submenu, index) => (
                        <React.Fragment key={index}>{submenu}</React.Fragment>
                    ))}
                </Node>
            </Node>
        );
    }
    return null;
};

/**
 * Render Search result Body.
 * @param props - The Search Result Modal view props.
 * @param refineMenu - The Refine Menu view props.
 * @param sortByDropDown - The Sort by view props.
 * @param isRecoSearchPage - The recomandation search page flag.
 * @returns The JSX Element.
 */
const createModalBody = (
    props: ISearchResultModalViewProps,
    refineMenu: IRefineMenuViewProps,
    sortByDropDown: ISortByViewProps,
    isRecoSearchPage?: string
): JSX.Element | null => {
    if (sortByDropDown) {
        return React.cloneElement(props.modalBody, {}, renderSort(sortByDropDown, isRecoSearchPage), renderRefinerMobile(refineMenu));
    }
    return null;
};

/**
 * Render Refiner.
 * @param props - The Refine Menu view props.
 * @returns The JSX Element.
 */
const renderRefiner = (props: IRefineMenuViewProps): JSX.Element | null => {
    const { refiners, RefineMenuContainer, RefinerSectionContainer } = props;

    if (!refiners) {
        return null;
    }

    // const filteredRefiners = config.disablePrices
    //     ? refiners.filter(refiner => {
    //           if (!React.isValidElement(refiner)) {
    //               return true;
    //           }
    //           return refiner.props.productRefinerHierarchy.SourceValue !== ProductRefinerSource.Price;
    //       })
    //     : refiners;

    // Price filter is being deleted for now as per design decision
    // Waiting for MS solution - #2603121420000684
    const filteredRefiners = refiners.filter(refiner => {
        if (!React.isValidElement(refiner)) {
            return true;
        }
        return refiner.props.productRefinerHierarchy.SourceValue !== ProductRefinerSource.Price;
    });

    if (filteredRefiners.length === 0) {
        return null;
    }

    return (
        <Node {...RefinerSectionContainer}>
            <Node {...RefineMenuContainer}>
                {filteredRefiners.map((submenu, index) => (
                    <React.Fragment key={index}>{submenu}</React.Fragment>
                ))}
            </Node>
        </Node>
    );
};

/**
 * Render Refiner Footer.
 * @param props - The Refine Menu view props.
 * @param handler - The Refine menu toggle function.
 * @param resources - Resources to get the done text and aria label.
 * @returns The JSX Element.
 */
const renderRefinerFooter = (
    props: IRefineMenuViewProps,
    handler: MouseEventHandler,
    resources: ISearchResultContainerResources
): JSX.Element | null => {
    const { refiners } = props;
    if (refiners) {
        return (
            <Node className='ms-refine__footer__done'>
                <Node tag='button' className='ms-refine-filter-done' aria-label={resources.doneAriaLabel} tabIndex={0} onClick={handler}>
                    {resources.doneText}
                </Node>
            </Node>
        );
    }
    return null;
};

/**
 * Render sort by drop down.
 * @param props - The sort by view props.
 * @param isRecoSearchPage - The recomandation search page flag.
 * @returns The JSX Element.
 */
const renderSort = (props: ISortByViewProps, isRecoSearchPage?: string): JSX.Element | null => {
    const { SortingContainer, sortByDropDown } = props;

    if (!sortByDropDown || isRecoSearchPage) {
        return null;
    }

    // Price sorting is being deleted for now as per design decision.
    // Waiting for MS solution - #2603121420000684
    if (React.isValidElement(sortByDropDown)) {
        const { dropdownOptions, ...restProps } = sortByDropDown.props;

        const filteredOptions = dropdownOptions.filter((option: { key: string }) => !option.key.toLowerCase().includes(filterPriceKeyword));

        const filteredDropdown = React.cloneElement(sortByDropDown, {
            ...restProps,
            dropdownOptions: filteredOptions
        });

        return <Node {...SortingContainer}>{filteredDropdown}</Node>;
    }

    return <Node {...SortingContainer}>{sortByDropDown}</Node>;
};

/**
 * Render Title.
 * @param props - The title view props.
 * @returns The JSX Element.
 */
const renderTitle = (props: ITitleViewProps): JSX.Element | null => {
    const { title, TitleContainer } = props;
    if (title) {
        return (
            <Node {...TitleContainer}>
                <Node tag='h3' className=''>
                    {title.titlePrefix}
                    {title.titleText}
                </Node>
            </Node>
        );
    }
    return null;
};

export default SearchResultContainerView;
